import { ArrowRight, BookOpen, Code, Zap, Award, Star, Clock, CheckCircle, ArrowDown, TestTube } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { trackCourseClick } from '../components/GoogleAnalytics';
import '../styles/scrollAnimations.css';

const SeleniumCSharpPathPage = () => {
  const courses = [
    {
      id: 1,
      title: "Selenium with C# .NET for Absolute Beginners",
      subtitle: "Foundation Course - Start Here",
      description: "Complete beginner-friendly course on Selenium automation using C# .NET. Learn web automation fundamentals, basic framework concepts, and essential testing practices.",
      longDescription: "Perfect starting point for Selenium automation with C# .NET. Cover automation basics, web element interaction, basic framework concepts, and fundamental testing practices.",
      level: "Beginner",
      duration: "10 hours",
      students: "24,500+",
      rating: 4.6,
      price: "Enroll on Udemy",
      icon: TestTube,
      color: "from-green-500 to-emerald-500",
      borderColor: "border-green-200 hover:border-green-500/50",
      bgColor: "bg-green-50 hover:bg-green-100",
      skills: ["Selenium Basics", "Web Element Interaction", "Basic Framework", "Test Management", "C# .NET", "Visual Studio"],
      udemy: "selenium-dotnet-basics",
      whatYoullLearn: [
        "Selenium automation fundamentals with C#",
        "Web element identification and interaction",
        "Basic test framework setup and management",
        "Essential debugging techniques and best practices",
        "Visual Studio IDE for automation development"
      ]
    },
    {
      id: 2,
      title: "Automation Framework Development with Selenium C# .NET",
      subtitle: "Framework Development",
      description: "Advanced Selenium automation framework development using C# .NET. Build enterprise-ready frameworks with design patterns, parallel execution, and comprehensive reporting.",
      longDescription: "Master advanced Selenium automation framework development with C# .NET. Cover framework architecture, design patterns, data management, parallel execution, custom reporting, and CI/CD integration.",
      level: "Advanced",
      duration: "16 hours",
      students: "18,300+",
      rating: 4.5,
      price: "Enroll on Udemy",
      icon: Code,
      color: "from-blue-500 to-cyan-500",
      borderColor: "border-blue-200 hover:border-blue-500/50",
      bgColor: "bg-blue-50 hover:bg-blue-100",
      skills: ["Framework Design", "Page Factory Pattern", "Data-Driven Testing", "Parallel Execution", "Custom Reporting", "NUnit"],
      udemy: "framework-development-with-selenium-csharp-advanced",
      whatYoullLearn: [
        "Design scalable Selenium automation frameworks",
        "Implement advanced design patterns (Page Object, Factory)",
        "Master parallel test execution strategies",
        "Create comprehensive test reporting solutions",
        "Integrate frameworks with build pipelines"
      ]
    },
    {
      id: 3,
      title: "Advanced Automation Testing of Modern Apps with C# .NET",
      subtitle: "Modern Applications Testing",
      description: "Modern application testing strategies using C# .NET. Learn to test SPAs, PWAs, microservices, and cloud-native applications with advanced automation techniques.",
      longDescription: "Comprehensive course on testing modern applications including SPAs, PWAs, microservices, and cloud-native apps. Cover advanced testing strategies, containerized testing, and modern CI/CD practices.",
      level: "Advanced",
      duration: "19 hours",
      students: "15,200+",
      rating: 4.6,
      price: "Enroll on Udemy",
      icon: Zap,
      color: "from-purple-500 to-pink-500",
      borderColor: "border-purple-200 hover:border-purple-500/50",
      bgColor: "bg-purple-50 hover:bg-purple-100",
      skills: ["Modern App Testing", "SPA Testing", "PWA Testing", "Performance Testing", "Security Testing", "Cloud Testing"],
      udemy: "modern-dotnet-testing",
      whatYoullLearn: [
        "Test modern application architectures (SPAs, PWAs)",
        "Implement containerized testing strategies",
        "Master cloud-native testing approaches",
        "Performance and security testing integration",
        "Advanced CI/CD pipeline integration"
      ]
    },
    {
      id: 4,
      title: "Event-Driven Microservice: Build and Test Apps with C# .NET",
      subtitle: "Microservices Testing Mastery",
      description: "Learn to build and test event-driven microservices using C# .NET. Master microservice architecture, event sourcing, message queues, and comprehensive testing strategies.",
      longDescription: "Deep dive into event-driven microservice architecture with C# .NET. Cover event sourcing, CQRS patterns, message brokers, service communication, and testing methodologies for distributed systems.",
      level: "Expert",
      duration: "20 hours",
      students: "9,800+",
      rating: 4.7,
      price: "Enroll on Udemy",
      icon: Award,
      color: "from-orange-500 to-red-500",
      borderColor: "border-orange-200 hover:border-orange-500/50",
      bgColor: "bg-orange-50 hover:bg-orange-100",
      skills: ["Microservice Architecture", "Event Sourcing", "Message Queues", "Integration Testing", "Contract Testing", "Docker"],
      udemy: "testing-eda-microservices",
      whatYoullLearn: [
        "Build event-driven microservice architectures",
        "Implement event sourcing and CQRS patterns",
        "Master microservice testing strategies",
        "Handle service-to-service communication",
        "Deploy and monitor distributed systems"
      ]
    }
  ];

  // Scroll animation hooks for each course
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const courseRefs = courses.map(() => useScrollAnimation({ threshold: 0.2, delay: 200 }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className={`relative bg-gradient-to-br from-green-500 via-blue-600 to-purple-600 text-white overflow-hidden hero-scroll-fade ${heroVisible ? 'animate' : ''}`}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/20 to-purple-900/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
                <TestTube className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Selenium with <span className="text-green-100">C# .NET</span> Learning Path
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-green-50 max-w-4xl mx-auto leading-relaxed mb-6 md:mb-8 px-2">
              Master Selenium automation from beginner to expert. Build enterprise frameworks, test modern applications, 
              and become a C# .NET automation expert.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
              <div className="flex items-center gap-2 text-green-100 text-sm sm:text-base">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                <span className="font-semibold">67,800+ students already enrolled</span>
              </div>
              <div className="flex items-center gap-2 text-green-100 text-sm sm:text-base">
                <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>4 comprehensive courses</span>
              </div>
              <div className="flex items-center gap-2 text-green-100 text-sm sm:text-base">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>65 hours total content</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Path */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Path Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Your <span className="bg-gradient-to-r from-green-600 to-purple-600 bg-clip-text text-transparent">Progressive Journey</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
              Follow this carefully designed path from Selenium basics to microservice testing mastery
            </p>
          </div>

          {/* Course Path */}
          <div className="relative">
            {/* Central Path Line - Hidden on mobile */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-0.5 top-0 bottom-0 w-1 bg-gradient-to-b from-green-500 via-blue-500 via-purple-500 to-orange-500 opacity-30"></div>

            {courses.map((course, index) => {
              const Icon = course.icon;
              const isEven = index % 2 === 0;
              const { ref, isVisible } = courseRefs[index];
              
              return (
                <div key={course.id} className="relative mb-8 md:mb-16 last:mb-0">
                  {/* Course Card */}
                  <div 
                    ref={ref}
                    className={`section-reveal ${isVisible ? 'animate' : ''} ${isEven ? 'md:pr-8 md:ml-0 md:text-right' : 'md:pl-8 md:ml-auto md:text-left'} md:w-1/2 w-full`}
                  >
                    <div className={`group relative bg-white rounded-xl md:rounded-2xl p-4 md:p-8 border-2 ${course.borderColor} transition-all duration-500 hover:shadow-2xl transform hover:scale-[1.02] cursor-pointer ${course.bgColor}`}>
                      
                      {/* Course Number Badge */}
                      <div className={`absolute ${isEven ? 'md:-right-4' : 'md:-left-4'} -top-3 md:-top-4 right-4 md:right-auto w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r ${course.color} rounded-full flex items-center justify-center text-white font-bold text-base md:text-lg shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        {index + 1}
                      </div>

                      {/* Course Header */}
                      <div className={`flex items-start gap-3 md:gap-6 mb-4 md:mb-6 ${isEven ? 'md:flex-row-reverse' : ''}`}>
                        <div className={`p-3 md:p-4 rounded-lg md:rounded-xl bg-gradient-to-r ${course.color} shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 md:gap-3 mb-2 flex-wrap">
                            <span className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ${
                              course.level === 'Beginner' ? 'bg-green-100 text-green-700' :
                              course.level === 'Intermediate' ? 'bg-blue-100 text-blue-700' :
                              course.level === 'Advanced' ? 'bg-purple-100 text-purple-700' :
                              'bg-orange-100 text-orange-700'
                            }`}>
                              {course.level}
                            </span>
                            <span className="text-gray-500 text-xs md:text-sm flex items-center gap-1">
                              <Clock className="w-3 h-3 md:w-4 md:h-4" />
                              {course.duration}
                            </span>
                          </div>
                          <div className="mb-2">
                            <span className="text-gray-500 text-xs md:text-sm flex items-center gap-1">
                              <Star className="w-3 h-3 md:w-4 md:h-4 text-yellow-400" />
                              {course.rating} ({course.students} students)
                            </span>
                          </div>
                          
                          <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-1 md:mb-2 leading-tight">{course.title}</h3>
                          <p className="text-base md:text-lg font-semibold text-green-600 mb-2 md:mb-3">{course.subtitle}</p>
                        </div>
                      </div>

                      {/* Course Description */}
                      <p className="text-sm md:text-base text-gray-700 mb-4 md:mb-6 leading-relaxed">{course.description}</p>

                      {/* What You'll Learn */}
                      <div className="mb-4 md:mb-6">
                        <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">What You'll Learn:</h4>
                        <div className="space-y-2">
                          {course.whatYoullLearn.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex items-start gap-3">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Skills */}
                      <div className="mb-4 md:mb-6">
                        <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">Key Skills:</h4>
                        <div className="flex flex-wrap gap-1 md:gap-2">
                          {course.skills.map((skill, skillIndex) => (
                            <span 
                              key={skillIndex}
                              className="px-2 md:px-3 py-1 bg-gray-100 border border-gray-200 rounded-md md:rounded-lg text-xs md:text-sm text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Action Button */}
                      <a
                        href={`https://www.udemy.com/course/${course.udemy}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackCourseClick(course.title, `https://www.udemy.com/course/${course.udemy}/`)}
                        className={`inline-flex items-center px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r ${course.color} text-white font-semibold rounded-lg md:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group/btn text-sm md:text-base`}
                      >
                        <BookOpen className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                        {course.price}
                        <ArrowRight className="w-3 h-3 md:w-4 md:h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                      </a>
                    </div>
                  </div>

                  {/* Path Connector - Hidden on mobile */}
                  {index < courses.length - 1 && (
                    <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 -bottom-8 z-10">
                      <div className={`w-8 h-8 bg-gradient-to-r ${course.color} rounded-full flex items-center justify-center shadow-lg`}>
                        <ArrowDown className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Completion Section */}
          <div className="mt-12 md:mt-20 bg-gradient-to-r from-green-500 to-purple-600 rounded-xl md:rounded-2xl p-6 md:p-8 text-white text-center">
            <div className="max-w-3xl mx-auto">
              <Award className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 md:mb-6" />
              <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">Selenium C# .NET Expert! 🎉</h3>
              <p className="text-base md:text-xl text-green-100 mb-4 md:mb-6 leading-relaxed">
                Congratulations! You'll master everything from basic Selenium automation to testing complex 
                microservice architectures with C# .NET. Ready to lead automation initiatives!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <Link
                  to="/automation"
                  className="bg-white text-green-600 px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold hover:bg-green-50 transition-colors text-sm md:text-base"
                >
                  Explore All Automation Courses
                </Link>
                <Link
                  to="/paths/playwright"
                  className="border-2 border-white text-white px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors text-sm md:text-base"
                >
                  Try Playwright Path
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeleniumCSharpPathPage;